import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchJobBoardCandidate,
  searchMonster,
  MonsterSearch,
  monsterPowerSearch,
} from 'wisestep-api';
import { IFilterList, WsFilterTags, WsListingLoader, WsPagination } from 'wisestep-base';
import X2JS from 'x2js';
import * as yup from 'yup';

import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import basicSearchDefaultValues, { generateJobBoardURL } from '../utils/nativeSearchHelper';

import AdvancedFiltersNew from './components/AdvancedFiltersNew';
import { AdvancedSearchKeys } from './types/AdvancedSearchKeys';
import MonsterCandidates from './types/MonsterCandidates';
import MonsterResponse from './types/MonsterResponse';
import constructQuery, { constructPSRXQuery } from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import { getMonsterCandidateDetails } from './utils/MonsterHelper';
import { processMonsterResponse } from './utils/processMonsterResponse';
import { pushSearchFilters } from './utils/pushSearchTags';

const MonsterSearchListNew = () => {
  const [params] = useSearchParams();
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const [monsterResponse, setMonsterResponse] = useState<MonsterResponse>({
    candidates: [],
  });
  const methods = useForm<BasicSearchForm & MonsterSearch>({
    defaultValues: {
      ...basicSearchDefaultValues,
      ...defaultAdvanceFilterValues,
      lastActive: '43200',
      sort: 'rank',
      searchType: params.get('loc')?.includes('us') ? 'Semantic' : 'Boolean',
      searchCriteria: 'zip',
      jobTitles: [],
      skills: [],
      queryType: 'Semantic',
    },
    resolver: yupResolver(
      yup.object().shape({
        query: yup
          .string()
          .when('searchType', {
            is: 'Boolean',
            then: yup.string().required('Search query is required'),
          })
          .when('queryType', {
            is: 'Boolean',
            then: yup.string().required('Search query is required'),
          }),
        jobTitles: yup.array().when('queryType', {
          is: 'Semantic',
          otherwise: yup.array().when('searchType', {
            is: 'Semantic',
            then: yup.array().required('Job title is required'),
          }),
          then: yup.array().nullable(false).min(1, 'Job title is required'),
        }),
        locations: yup
          .array()
          .when('searchType', {
            is: 'Semantic',
            then: yup.array().max(1, ' one location is allowed'),
          })
          .when(['searchCriteria', 'zipcode'], {
            is: (searchCriteria, zipcode) => searchCriteria === 'country' && !isEmpty(zipcode),
            then: yup.array().max(1, 'only one country is allowed with zip code'),
          }),
      })
    ),
  });
  const searchType = methods.watch('searchType');
  const queryType = methods.watch('queryType');
  useEffect(() => {
    methods.reset({
      ...basicSearchDefaultValues,
      ...defaultAdvanceFilterValues,
      searchType: params.get('loc')?.includes('us') ? searchType : 'Boolean',
      lastActive: searchType == 'Boolean' ? '43200' : '',
      sort: 'rank',
      searchCriteria: 'zip',
      accountId: methods.getValues('accountId'),
      jobTitles: [],
      skills: [],
      distance: searchType == 'Boolean' ? 50 : 30,
      queryType:
        params.get('loc')?.includes('us') && searchType === 'Semantic' ? 'Semantic' : 'Boolean',
    });
  }, [methods, searchType, params]);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const performSearch = async (values: BasicSearchForm & MonsterSearch) => {
    setAppliedFilters(pushSearchFilters(values));
    const query =
      searchType === 'Boolean' || queryType === 'Boolean'
        ? ` ${
            methods
              .getValues('qrjtArr')
              ?.map((title) => JSON.stringify(title))
              ?.join(',') ?? ''
          } ${
            methods
              .getValues('qtjtArr')
              ?.map((title) => JSON.stringify(title))
              ?.join(',') ?? ''
          } ${methods.getValues('query')} ` ?? ''
        : `${
            methods
              .getValues('jobTitles')
              ?.map((title) => JSON.stringify(title))
              ?.join(',') ?? ''
          } ${
            methods
              .getValues('skills')
              ?.map((title) => JSON.stringify(title))
              ?.join(',') ?? ''
          }`;
    setSearchQuery(query);
    setIsLoading(true);
    try {
      const emptyResponse: MonsterResponse = {
        candidates: [],
      };

      setMonsterResponse(emptyResponse);
      const searchCriteria = methods.watch('searchCriteria');
      let data;
      if (values.searchType === 'Boolean') {
        data = await searchMonster(constructQuery(values, searchCriteria));
      } else {
        data = await monsterPowerSearch(constructPSRXQuery(values));
      }
      if (data.data && (data.data.statusCode == '400' || data.data.statusCode == '504')) {
        const errorMessage = !isEmpty(values.zipcode)
          ? 'no results found with given zip code'
          : (data.data.status as string);
        setNotification(failed(errorMessage));
        setIsLoading(false);
        return;
      }
      const x2js = new X2JS();
      const searchRegExp = /&#([A-Za-z0-9])+;/g;
      const document = x2js.xml2js(data.data.response.replaceAll(searchRegExp, ''));
      const response = processMonsterResponse(document);
      response.totalItems = data.data.totalResults;
      setMonsterResponse(response);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);

  const isDownloaded = (candidate: MonsterCandidates): boolean => {
    return candidate._Downloaded ?? false;
  };
  const accountId = methods.watch('accountId');
  const setNotification = useNotificationStore((state) => state.setNotification);
  useEffect(() => {
    const message =
      methods.formState.errors.query?.message ??
      methods.formState.errors.jobTitles?.[0]?.message ??
      methods.formState.errors.jobTitles?.['message'];

    if (message) {
      setNotification(failed(message));
    }
  }, [methods.formState.errors.jobTitles, methods.formState.errors.query, setNotification]);
  const onDeleteFilterTag = (tag: string) => {
    const idOfChip = appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, defaultAdvanceFilterValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = appliedFilters.findIndex((existChip) => existChip.id === idOfChip);
      if (!['allAny', 'workAuthorizations', 'preferredEmploymentTypes'].includes(idOfChip))
        appliedFilters.splice(index, 1);
      else {
        const dataObj = {};
        dataObj[idOfChip] = defaultAdvanceFilterValues[idOfChip];
        appliedFilters[index] =
          pushSearchFilters(defaultAdvanceFilterValues).find(
            (filterTag) => filterTag.id === idOfChip
          ) ?? ({} as IFilterList);
      }
      setAppliedFilters([...appliedFilters]);
    }
  };
  return (
    <FormProvider {...methods}>
      <Grid container display="flex" justifyContent="space-between">
        <Grid item xs={9}>
          <Box ml={2}>
            <NativeSearchForm jobBoardName="MONSTER" performSearch={performSearch} />
          </Box>
          <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
          {monsterResponse.totalItems && monsterResponse.totalItems > 0 && (
            <WsPagination
              count={Math.ceil(monsterResponse.totalItems / 50)}
              onChange={(_, value) => {
                methods.setValue('page', value + 1);
                methods.handleSubmit(performSearch)();
              }}
              itemsPerPage={50}
              pageNumber={methods.watch('page') - 1}
              defaultPage={methods.watch('page')}
              isPageCount={true}
              type="Candidates"
              totalCount={monsterResponse.totalItems}
            />
          )}
          {isLoading && <WsListingLoader />}
          {!isLoading && monsterResponse?.totalItems && monsterResponse?.totalItems < 1 && (
            <Box my={2}>
              <NoDataFoundCard text="No Candidates Found" />
            </Box>
          )}
          {!isLoading &&
            monsterResponse.candidates.map((candidate) => (
              <Grid my={2} key={candidate._SID} item xs={12}>
                <CandidateCard
                  candidateId={candidate._SID}
                  title={candidate.candidateData.name ?? ''}
                  name={candidate.candidateData.name ?? ''}
                  imgSrc={''}
                  showRelevance={true}
                  relevance={candidate.candidateData.relevance ?? ''}
                  isDownloaded={isDownloaded(candidate)}
                  skills={candidate.candidateData.skills}
                  dateCreated={candidate.DateCreated.__text ?? ''}
                  dateResumeLastUpdated={candidate.LastUpdated}
                  skillsDisplayCount={24}
                  onViewCvClick={async (candidateId) => {
                    try {
                      const data = await fetchJobBoardCandidate({
                        jobBoardAccountId: accountId,
                        jobBoardId: candidateId,
                        force: false,
                        harvestingId: '',
                        candidateData: candidate.candidateData,
                        searchType: searchType,
                      });
                      const candidateData = data.newCandidateData;
                      localStorage.setItem(
                        candidateId,
                        getEncodedQueryParams(candidate.candidateData)
                      );
                      const query =
                        searchType === 'Boolean' || queryType === 'Boolean'
                          ? ` ${
                              methods
                                .getValues('qrjtArr')
                                ?.map((title) => JSON.stringify(title))
                                ?.join(',') ?? ''
                            } ${
                              methods
                                .getValues('qtjtArr')
                                ?.map((title) => JSON.stringify(title))
                                ?.join(',') ?? ''
                            } ${methods.getValues('query')} ` ?? ''
                          : `${
                              methods
                                .getValues('jobTitles')
                                ?.map((title) => JSON.stringify(title))
                                ?.join(',') ?? ''
                            } ${
                              methods
                                .getValues('skills')
                                ?.map((title) => JSON.stringify(title))
                                ?.join(',') ?? ''
                            }`;
                      const params: IQueryParam = {
                        resumeUpdatedOn: candidate.LastUpdated,
                        accountId: accountId,
                        jobBoardId: candidateId,
                        query: encodeURI(query),
                        searchType: searchType,
                        jobBoard: 'Monster',
                      };
                      setCvViewSourcePage(ITrackCvViewJobBoards.MONSTER_CA);
                      const paramId = `param${uuidv4()}`;
                      localStorage.setItem(paramId, getEncodedQueryParams(params));
                      openUrlInNewTab(
                        `/candidate-layout/profile?id=${
                          candidateData?.id
                        }&array=${[]}&q=${getEncodedQueryParams(
                          params
                        )}&backwardpathArray=${getEncodedQueryParams(
                          JSON.stringify(generateJobBoardURL())
                        )}`
                      );
                    } catch (error) {
                      const err = error as AxiosError;
                      if (err.response?.data.status) {
                        setNotification(failed(err.response?.data.status));
                      } else {
                        setNotification(
                          failed('There was some problem in fetching the candidate.')
                        );
                      }
                    }
                  }}
                  detailItems={getMonsterCandidateDetails(candidate)}
                />
              </Grid>
            ))}
        </Grid>
        <Grid item xs={2.8}>
          <AdvancedFiltersNew performSearch={performSearch} />
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default MonsterSearchListNew;
